import { useRef, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  DeleteTwoTone as DeleteTwoToneIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  HelpOutline as HelpOutlineIcon,
  Label as LabelIcon,
  PlaylistAdd as PlaylistAddIcon,
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from '@material-ui/icons';
import {
  Alert,
  AlertTitle,
} from '@material-ui/lab';
import {
  Document,
  Page,
  pdfjs,
} from 'react-pdf';

import {
  ButtonGroup,
  DataTable,
  DynamicForm,
  Header,
  Modal,
} from 'components';
import { SelectControl } from 'components/DynamicForm/controls';
import { useTable } from 'hooks';
import {
  addSignerFormSchema,
  addSignerFormSchemaClient,
  addSignerFormSchemaRelationship,
} from 'validations/schema/forms';

import useStyles from './styles';
import {
  cleanString,
  cleanTemplate,
  DEFAULT_PROPS,
  filterValidClients,
  getSignersButtonGroup,
  getStepsQuestion1,
  getStepsQuestion2,
  HEADERS,
  isSignatureToken,
  PROP_TYPES,
  TYPE_MAP,
} from './constants';
import useBreakpoints from './hooks/useBreakpoints';
import useEditorTranslation from './hooks/useEditorTranslation';
import useFileUpload from './hooks/useFileUpload';
import useLabelsExtraction from './hooks/useLabelsExtraction';
import useLocalStoragePersistence from './hooks/useLocalStoragePersistence';
import usePdfViewer from './hooks/usePdfViewer';
import useSidebar from './hooks/useSidebar';
import useSignaturePlacement from './hooks/useSignaturePlacement';
import useSignersManagement from './hooks/useSignersManagement';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function FileAndHtmlControl(props) {
  const classes = useStyles();
  const { isMobile, isTablet, isDesktop } = useBreakpoints();
  const { t } = useEditorTranslation();
  const [previousConfiguration, setPreviousConfiguration] = useState(null);
  const [showPreserveConfiguration, setShowPreserveConfiguration] = useState(false);
  const previousConfigurationRef = useRef(null);
  const {
    numPages,
    pageNumber,
    pdfZoom,
    handleDocumentLoadSuccess,
    handleBack,
    handleNext,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
  } = usePdfViewer({
    isMobile,
    isTablet,
    isDesktop,
  });
  const {
    labels,
    labelsMatch,
    fieldFunction,
    openFunctions,
    newLabel,
    isAdding,
    setLabels,
    setLabelsMatch,
    setFieldFunction,
    setOpenFunctions,
    setNewLabel,
    setIsAdding,
    getTextFromPDF,
    signatureAnchors,
    labelsRevision,
    setLabelsRevision,
    handleAddLabel,
  } = useLabelsExtraction({
    defaultSignatures: props.defaultSignatures,
    defaultFunctions: props.defaultFunctions,
  });

  const {
    signers,
    data,
    modal,
    modal2,
    table,
    company,
    functions,
    currentView,
    isLoading,
    typeAddSigner,
    selectorTypes,
    selectorType,
    selectorClients,
    selectorClient,
    selectorRelationships,
    selectorRelationShip,
    isProcessing,
    valuesClient,
    setModal,
    setModal2,
    setCurrentView,
    setSelectorType,
    setSelectorClient,
    setSelectorRelationShip,
    setTypeAddSigner,
    handleModalClose,
    handleSubmitData,
    handleRemoveSigner,
    handleSelectorType,
    handleSelectorClient,
    handleSelectorRelationships,
  } = useSignersManagement({
    t,
  });

  const {
    signatures,
    dimensions,
    menuPosition,
    showSigners,
    showTypeSigners,
    draggingSignature,
    dragOffset,
    setSignatures,
    setDraggingSignature,
    setShowSigners,
    setShowTypeSigners,
    handleMenuOption,
    handleRemoveSignature,
    handleResizeMouseDown,
    handleAssignSignature,
    preserveSignatures,
  } = useSignaturePlacement({
    defaultSignatures: props.defaultSignatures,
    pageNumber,
    setTypeAddSigner,
  });
  const { generateTable } = useTable({
    translate: t,
  });
  const SIGNERS_BUTTON_GROUP = getSignersButtonGroup(t);
  const {
    openSidebar,
    tabIndex,
    setTabIndex,
    handleSidebarToggle,
  } = useSidebar({
    defaultFile: props.defaultFile,
  });
  const stepsQuestion1 = getStepsQuestion1(t);
  const stepsQuestion2 = getStepsQuestion2(t);

  const {
    disabled,
    label,
    size,
    settings,
    readOnly,
  } = props;
  const { accept = [], base64 = false, multiple = false } = settings;

  const handleBeforeFileChange = () => {
    const hasPreviousConfiguration = Object.keys(labelsMatch || {}).length > 0
      || (signatures || []).length > 0;

    if (!hasPreviousConfiguration) return;

    const snapshot = {
      labelsMatch: labelsMatch || {},
      fieldFunction: fieldFunction || {},
      signatures: signatures || [],
    };

    previousConfigurationRef.current = snapshot;
    setPreviousConfiguration(snapshot);
  };

  const handleDocumentAnalyzed = () => {
    if (previousConfigurationRef.current) {
      setShowPreserveConfiguration(true);
    }
  };

  const handlePreserveConfiguration = () => {
    const previousLabels = previousConfiguration?.labelsMatch || {};
    const previousFunctions = previousConfiguration?.fieldFunction || {};
    const nextLabels = Object.keys(labelsMatch || {}).reduce((result, key) => ({
      ...result,
      [key]: previousLabels[key] ?? '',
    }), {});
    const nextFunctions = Object.keys(labelsMatch || {}).reduce((result, key) => ({
      ...result,
      [key]: previousFunctions[key] || [],
    }), {});

    setLabels(nextLabels);
    setLabelsMatch(nextLabels);
    setFieldFunction(nextFunctions);
    setLabelsRevision((revision) => revision + 1);
    preserveSignatures(previousConfiguration?.signatures, signatureAnchors);
    setShowPreserveConfiguration(false);
    previousConfigurationRef.current = null;
    setPreviousConfiguration(null);
  };

  const handleDiscardPreviousConfiguration = () => {
    setShowPreserveConfiguration(false);
    previousConfigurationRef.current = null;
    setPreviousConfiguration(null);
  };

  let inputFile;

  const {
    text,
    currentError,
    pdfFile,
    isUploading,
    showMobileAlert,
    isDragging,
    setShowMobileAlert,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDownloadPdf,
  } = useFileUpload({
    defaultFile: props.defaultFile,
    defaultFileName: props.defaultFileName,
    name: props.name,
    onChange: props.onChange,
    events: props.events,
    accept,
    base64,
    error: props.error,
    isMobile,
    getTextFromPDF,
    onBeforeFileChange: handleBeforeFileChange,
    onDocumentAnalyzed: handleDocumentAnalyzed,
    company,
    setSignatures,
    handleSidebarToggle,
  });

  const stepsCompleted = [
    pdfFile !== null,
    labels && Object.keys(labels).length > 0,
    labels && Object.values(labels).every((value) => value !== ''),
    signatures && Object.keys(signatures).length > 0,
  ];

  //Detectar si se ha seleccionado un archivo
  const handleSelectFile = function () {
    inputFile.click();
  };

  useLocalStoragePersistence({
    signatures,
    dimensions,
    labels,
    labelsMatch,
    fieldFunction,
  });

  return (
    <>
      <Modal
        open={showPreserveConfiguration}
        title="Conservar configuración anterior"
        actions={[
          {
            color: 'secondary',
            label: 'No, empezar de cero',
            fn: handleDiscardPreviousConfiguration,
          },
          {
            color: 'primary',
            label: 'Sí, conservar coincidencias',
            fn: handlePreserveConfiguration,
          },
        ]}
        events={{
          onClose: handleDiscardPreviousConfiguration,
        }}
      >
        <Typography variant="body1">
          Se encontraron configuraciones del documento anterior. ¿Quieres conservar
          los mapeos y firmantes que coincidan con el nuevo documento?
        </Typography>
      </Modal>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          margin: 'auto',
        }}
      >
        <Grid
          container
          sx={{
            width: '100%',
          }}
        >
          <Grid item md={openSidebar ? 8 : 12} xs={12}>
            <Box
              sx={{
                flexGrow: 1,
                transition: 'margin-right 0.3s ease',
                marginRight: openSidebar ? '16px' : '0px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#f1f3f4',
                  borderRadius: '8px',
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.2)',
                  padding: 1,
                }}
              >
                {/* Barra de herramientas */}
                {!readOnly && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 1 : 2,
                      padding: 1,
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                    }}
                  >
                    {pdfFile && (
                      <>
                        <IconButton
                          onClick={() => {
                            handleSelectFile();
                          }}
                          title={t('actions.upload_pdf')}
                        >
                          <CloudUploadIcon />
                        </IconButton>
                        <IconButton
                          disabled={!pdfFile}
                          onClick={() => {
                            handleDownloadPdf();
                          }}
                          title={t('actions.download_pdf')}
                        >
                          <CloudDownloadIcon />
                        </IconButton>
                        <input
                          type="file"
                          accept=".pdf, .docx"
                          hidden
                          ref={inputFile}
                          onChange={handleFileChange}
                        />
                        <Divider orientation="vertical" flexItem />
                      </>
                    )}
                    <Box
                      onClick={() => handleSidebarToggle(0, true)}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton>
                        <LabelIcon />
                      </IconButton>
                      {!isMobile && (
                        <Typography
                          variant="caption"
                          className={classes.labelBar}
                        >
                          {t('actions.label')}
                        </Typography>
                      )}
                      {isMobile && (
                        <Typography
                          variant="caption"
                          style={{
                            fontSize: '0.6rem',
                            marginTop: -4,
                          }}
                        >
                          {t('actions.label')}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      onClick={() =>
                        setModal({
                          title: 'Firmantes',
                          open: true,
                          maxWidth: 'lg',
                          view: 'addSigner',
                        })
                      }
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton>
                        <GroupIcon />
                      </IconButton>
                      {!isMobile && (
                        <Typography
                          variant="caption"
                          className={classes.labelBar}
                        >
                          {t('actions.signers')}
                        </Typography>
                      )}
                      {isMobile && (
                        <Typography
                          variant="caption"
                          style={{
                            fontSize: '0.6rem',
                            marginTop: -4,
                          }}
                        >
                          {t('actions.signers')}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      onClick={() => handleSidebarToggle(1, true)}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton>
                        <HelpOutlineIcon />
                      </IconButton>
                      {!isMobile && (
                        <Typography
                          variant="caption"
                          className={classes.labelBar}
                        >
                          {t('actions.help')}
                        </Typography>
                      )}
                      {isMobile && (
                        <Typography
                          variant="caption"
                          style={{
                            fontSize: '0.6rem',
                            marginTop: -4,
                          }}
                        >
                          {t('actions.help')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Alert para dispositivos móviles */}
              {isMobile && pdfFile && (
                <Collapse in={showMobileAlert}>
                  <Alert
                    severity="info"
                    onClose={() => setShowMobileAlert(false)}
                    style={{
                      marginTop: '16px',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                    }}
                  >
                    <AlertTitle
                      style={{
                        fontSize: '.85rem',
                      }}
                    >
                      💡 {t('messages.mobile_recommendation_title')}
                    </AlertTitle>
                    {t('messages.mobile_recommendation_text')}
                  </Alert>
                </Collapse>
              )}

              <Box
                sx={{
                  marginTop: 16,
                }}
              >
                <FormControl
                  disabled={disabled}
                  error={currentError}
                  fullWidth
                  size={size}
                  variant="outlined"
                >
                  {isUploading ? (
                    <div className={classes.uploadContainer}>
                      <CircularProgress
                        className={classes.uploadIcon}
                        size={90}
                      />
                      <p className={classes.uploadLabel}>
                        Obteniendo archivo...
                      </p>
                      {text ? (
                        <p className={classes.uploadTextAlt}>{text}</p>
                      ) : (
                        <p className={classes.uploadText}>
                          Por favor espera...
                        </p>
                      )}
                    </div>
                  ) : !pdfFile ? (
                    <div
                      className={classes.uploadContainer}
                      onClick={handleSelectFile}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      style={{
                        border: isDragging
                          ? '2px dashed #1976d2'
                          : '2px dashed transparent',
                        backgroundColor: isDragging
                          ? 'rgba(53, 116, 179, 0.1)'
                          : undefined,
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      }}
                    >
                      <input
                        accept={accept.map((_) => `.${_}`).join()}
                        hidden
                        multiple={multiple}
                        onChange={handleFileChange}
                        ref={(input) => (inputFile = input)}
                        type="file"
                      />
                      <CloudUploadOutlinedIcon className={classes.uploadIcon} />
                      <p className={classes.uploadLabel}>{label}</p>
                      {text ? (
                        <p className={classes.uploadTextAlt}>{text}</p>
                      ) : (
                        <p className={classes.uploadText}>
                          {isDragging
                            ? 'Suelta el archivo aquí'
                            : 'Presiona o arrastra un archivo'}
                        </p>
                      )}
                      {currentError && (
                        <p className={classes.errorText}>{currentError}</p>
                      )}
                    </div>
                  ) : (
                    pdfFile && (
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={10} md={10}>
                          <input
                            accept={accept.map((_) => `.${_}`).join()}
                            hidden
                            multiple={multiple}
                            onChange={handleFileChange}
                            ref={(input) => (inputFile = input)}
                            type="file"
                          />
                          {currentError && (
                            <FormHelperText>{currentError}</FormHelperText>
                          )}
                        </Grid>
                      </Grid>
                    )
                  )}
                </FormControl>
              </Box>
              {/*Muestra el pdf*/}
              {!isUploading && pdfFile && (
                <Grid container justifyContent="center" >
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: isMobile ? '8px' : '16px',
                    }}
                  >
                    <div className={classes.pdfContainer}>
                      <Document
                        file={pdfFile}
                        onLoadSuccess={handleDocumentLoadSuccess}
                      >
                        <Page
                          pageNumber={pageNumber}
                          className={classes.pdfViewer}
                          onClick={undefined}
                          scale={pdfZoom}
                          onMouseMove={(e) => {
                            if (draggingSignature !== null) {
                              e.preventDefault(); // Evitar la selección de texto mientras se arrastra
                              const rect = e.target.getBoundingClientRect();
                              const x = e.clientX - rect.left;
                              const y = e.clientY - rect.top;
                              setSignatures((prev) =>
                                prev.map((sig) =>
                                  sig.id === draggingSignature ? {
                                    ...sig,
                                    relative_position_x: x,
                                    relative_position_y: y,
                                  } : sig,
                                ),
                              );
                            }
                          }}
                          onMouseUp={() => setDraggingSignature(null)}
                        >
                          {menuPosition && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${menuPosition.relative_position_x}px`,
                                top: `${menuPosition.relative_position_y}px`,
                                background: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                boxShadow: '0px 4px 10px rgba(0,0,0,0.2)',
                                padding: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                zIndex: 10,
                              }}
                              onClick={(e) => e.stopPropagation()} // Evita que se cierre al hacer clic dentro del menú
                            >
                              {!showTypeSigners && !showSigners ? (
                                <>
                                  <p
                                    style={{
                                      margin: '0',
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      fontFamily: 'Roboto',
                                      backgroundColor: '#bfbfbf',
                                      padding: '8px',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    {t('actions.options')}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleMenuOption('addSignature')
                                    }
                                    className={classes.addSignatureButton}
                                  >
                                    {t('actions.add_signature')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuOption('cancel');
                                    }}
                                    className={classes.cancelButton}
                                  >
                                    {t('actions.cancel')}
                                  </button>
                                </>
                              ) : showTypeSigners && !showSigners ? (
                                <>
                                  <p
                                    style={{
                                      margin: '0',
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      fontFamily: 'Roboto',
                                      backgroundColor: '#bfbfbf',
                                      padding: '8px',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    {t('actions.types_signers')}
                                  </p>
                                  {selectorTypes.map((type) => (
                                    <button
                                      type="button"
                                      key={type.id}
                                      onClick={() =>
                                        handleMenuOption(
                                          'selectTypeSigner',
                                          type,
                                        )
                                      }
                                      className={classes.addSignatureButton}
                                    >
                                      {type.name}
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuOption('cancel');
                                    }}
                                    className={classes.cancelButton}
                                  >
                                    {t('actions.cancel')}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <p
                                    style={{
                                      margin: '0',
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      fontFamily: 'Roboto',
                                      backgroundColor: '#bfbfbf',
                                      padding: '8px',
                                      borderRadius: '8px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {t('actions.select_signer')}
                                  </p>
                                  {signers.filter(
                                    (signer) =>
                                      signer.type === TYPE_MAP[typeAddSigner],
                                  ).length > 0 ? (
                                      signers
                                        .filter(
                                          (signer) =>
                                            signer.type === TYPE_MAP[typeAddSigner],
                                        )
                                        .map((signer) => (
                                          <button
                                            type="button"
                                            key={signer.id}
                                            onClick={() =>
                                              handleMenuOption(
                                                'selectSigner',
                                                signer,
                                              )
                                            }
                                            className={classes.addSignatureButton}
                                          >
                                            {signer.name}
                                          </button>
                                        ))
                                    ) : (
                                      <p
                                        style={{
                                          fontSize: '16px',
                                          fontFamily: 'Roboto',
                                          textAlign: 'center',
                                        }}
                                      >
                                        {t('messages.no_signers_available')}
                                      </p>
                                    )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowSigners(false); // Volver al menú anterior
                                      setShowTypeSigners(true);
                                    }}
                                    className={classes.cancelButton}
                                  >
                                    {t('actions.back')}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {signatures
                            .filter(
                              (signature) => signature.page === pageNumber,
                            )
                            .map((signature) => (
                              <div
                                key={signature.id}
                                style={{
                                  position: 'absolute',
                                  left: `${signature.relative_position_x}${signature.sign ? '%' : 'px'}`,
                                  top: `${signature.relative_position_y}${signature.sign ? '%' : 'px'}`,
                                  width: `${signature.relative_size_x || 90}${signature.sign ? '%' : 'px'}`,
                                  height: `${signature.relative_size_y || 40}${signature.sign ? '%' : 'px'}`,
                                  border: '2px dashed #007bff',
                                  backgroundColor: 'rgba(0, 123, 255, 0.1)',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  fontSize: '12px',
                                  color: '#007bff',
                                  cursor: 'grab', // Indicar que es arrastrable
                                  zIndex:
                                    draggingSignature === signature.id
                                      ? 20
                                      : 10,
                                  userSelect: 'none', // Evitar la selección de texto
                                }}
                                onMouseDown={(e) => {
                                  if (signature.sign) return;
                                  setDraggingSignature(signature.id);
                                  dragOffset.current = {
                                    relative_position_x:
                                      e.clientX - signature.relative_position_x,
                                    relative_position_y:
                                      e.clientY - signature.relative_position_y,
                                  };
                                }}
                                onClick={(e) => e.stopPropagation()} // Evitar que se cierre al hacer clic en la firma
                              >
                                {signature.name}
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: '0',
                                    right: '0',
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: '#007bff',
                                    cursor: 'se-resize',
                                  }}
                                  onMouseDown={(e) =>
                                    handleResizeMouseDown(e, signature.id)
                                  }
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveSignature(signature.id);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '-10px',
                                    right: '-10px',
                                    backgroundColor: '#85929e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  X
                                </button>
                              </div>
                            ))}
                        </Page>
                      </Document>
                    </div>
                  </Grid>
                  {/* Controles de zoom */}
                  <Grid item xs={12} className={classes.zoomControlsContainer}>
                    <button
                      type="button"
                      className={classes.zoomButton}
                      onClick={handleZoomOut}
                      disabled={pdfZoom <= 0.3}
                      title={t('actions.zoom_out') || 'Alejar'}
                    >
                      <ZoomOutIcon fontSize={isMobile ? 'small' : 'default'} />
                      {!isMobile && 'Alejar'}
                    </button>
                    <div className={classes.zoomInfo}>
                      {Math.round(pdfZoom * 100)}%
                    </div>
                    <button
                      type="button"
                      className={classes.zoomButton}
                      onClick={handleZoomIn}
                      disabled={pdfZoom >= 3.0}
                      title={t('actions.zoom_in') || 'Acercar'}
                    >
                      <ZoomInIcon fontSize={isMobile ? 'small' : 'default'} />
                      {!isMobile && 'Acercar'}
                    </button>
                    <button
                      type="button"
                      className={classes.zoomButton}
                      onClick={handleResetZoom}
                      title={t('actions.reset_zoom') || 'Tamaño original'}
                    >
                      <ZoomOutMapIcon
                        fontSize={isMobile ? 'small' : 'default'}
                      />
                      {!isMobile && 'Restablecer'}
                    </button>
                  </Grid>
                  <Grid item xs={12} className={classes.paginationContainer}>
                    <button
                      type="button"
                      className={classes.paginationButton}
                      onClick={handleBack}
                      disabled={pageNumber <= 1}
                      style={{
                        fontSize: isMobile ? '0.75rem' : '1rem',
                        padding: isMobile ? '8px 12px' : '12px 16px',
                      }}
                    >
                      {isMobile ? '‹' : t('actions.back_page')}
                    </button>
                    <span
                      style={{
                        fontSize: isMobile ? '0.8rem' : '1rem',
                        margin: isMobile ? '0 8px' : '0 16px',
                      }}
                    >
                      {isMobile
                        ? `${pageNumber}/${numPages}`
                        : `${t('actions.page')} ${pageNumber} ${t(
                          'actions.of',
                        )} ${numPages}`}
                    </span>
                    <button
                      type="button"
                      className={classes.paginationButton}
                      onClick={handleNext}
                      disabled={pageNumber >= numPages}
                      style={{
                        fontSize: isMobile ? '0.75rem' : '1rem',
                        padding: isMobile ? '8px 12px' : '12px 16px',
                      }}
                    >
                      {isMobile ? '›' : t('actions.next_page')}
                    </button>
                  </Grid>
                </Grid>
              )}
              {!readOnly && (
                <Modal
                  title={modal?.title}
                  open={modal?.open}
                  maxWidth={modal?.maxWidth}
                  events={{
                    onClose: () => handleModalClose(),
                  }}
                >
                  {modal?.view === 'addSigner' && (
                    <>
                      <ButtonGroup
                        actions={SIGNERS_BUTTON_GROUP}
                        onChange={(value) => {
                          setCurrentView(value);
                          setSelectorType(t('forms.selectors.select'));
                          setSelectorClient(t('forms.selectors.select'));
                          setSelectorRelationShip(t('forms.selectors.select'));
                        }}
                        pb={3}
                        pt={2}
                        value={currentView}
                        width={isMobile ? '100%' : 220}
                        color={'secondary'}
                      />
                      <Header
                        actions={[
                          {
                            color: 'secondary',
                            form: 'frmAddSigner',
                            icon: <SaveIcon />,
                            label: t('actions.save'),
                            type: 'submit',
                            processing: isLoading,
                            hidden: currentView !== 'add',
                          },
                        ]}
                        breadcrumb={false}
                        title={
                          currentView === 'add'
                            ? t('actions.create_signer')
                            : t('actions.list_signers')
                        }
                      />

                      {currentView === 'list' && (
                        <DataTable
                          actions={[
                            {
                              color: 'error',
                              icon: <DeleteTwoToneIcon />,
                              title: t('table.actions.delete_signer'),
                              fn: function ({ data }) {
                                setModal2({
                                  open: true,
                                  data,
                                });
                              },
                            },
                          ]}
                          total={table?.total ?? 0}
                          {...generateTable(
                            HEADERS.filter((header) => !header?.hidden),
                            table?.rows,
                          )}
                          mt={2}
                        />
                      )}

                      {currentView === 'add' && (
                        <>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={6}>
                              <SelectControl
                                onChange={function ({ target }) {
                                  handleSelectorType(target);
                                }}
                                label={t('forms.signers.fields.type')}
                                settings={{
                                  emptyElement: t('forms.selectors.select'),
                                  options: selectorTypes.map(
                                    ({ id, name }) => ({
                                      id: id,
                                      name: name,
                                    }),
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                              {selectorType === 2 && (
                                <SelectControl
                                  onChange={function ({ target }) {
                                    handleSelectorClient(target);
                                  }}
                                  key={selectorType}
                                  label={t('forms.signers.fields.templete')}
                                  settings={{
                                    emptyElement: t('forms.selectors.select'),
                                    options: selectorClients.map(
                                      ({ id, name }) => ({
                                        id: id,
                                        name: name,
                                      }),
                                    ),
                                  }}
                                />
                              )}
                              {selectorType === 3 && (
                                <SelectControl
                                  onChange={function ({ target }) {
                                    handleSelectorRelationships(target);
                                  }}
                                  key={selectorType}
                                  label={t('forms.signers.fields.relationship')}
                                  settings={{
                                    emptyElement: t('forms.selectors.select'),
                                    options: selectorRelationships.map(
                                      ({ id, name }) => ({
                                        id: id,
                                        name: name,
                                      }),
                                    ),
                                  }}
                                />
                              )}
                            </Grid>
                          </Grid>
                          {isProcessing === false && (
                            <DynamicForm
                              fields={[
                                {
                                  display: {
                                    breakpoints: {
                                      md: 6,
                                      sm: 12,
                                      xs: 12,
                                    },
                                  },
                                  type: 'divider',
                                  name: 'divider1',
                                },
                                {
                                  display: {
                                    breakpoints: {
                                      md: 6,
                                      sm: 12,
                                      xs: 12,
                                    },
                                  },
                                  label: t('forms.signers.fields.name'),
                                  name: 'name',
                                  type:
                                    selectorType !==
                                      t('forms.selectors.select') &&
                                    selectorType === 1
                                      ? 'text'
                                      : selectorType === 2 &&
                                        selectorClient !==
                                          t('forms.selectors.select')
                                        ? 'select'
                                        : selectorType === 3 &&
                                        selectorRelationShip !==
                                          t('forms.selectors.select')
                                          ? 'select'
                                          : 'hidden',
                                  settings: {
                                    autocomplete:
                                      (valuesClient || [])?.length > 10,
                                    emptyElement: t('forms.selectors.select'),
                                    options:
                                      filterValidClients(valuesClient) || [],
                                  },
                                },
                                {
                                  display: {
                                    breakpoints: {
                                      md: 6,
                                      sm: 12,
                                      xs: 12,
                                    },
                                  },
                                  label: t('forms.signers.fields.email'),
                                  name: 'email',
                                  type:
                                    selectorType !==
                                      t('forms.selectors.select') &&
                                    selectorType === 1
                                      ? 'text'
                                      : selectorType === 2 &&
                                        selectorClient !==
                                          t('forms.selectors.select')
                                        ? 'select'
                                        : selectorType === 3 &&
                                        selectorRelationShip !==
                                          t('forms.selectors.select')
                                          ? 'select'
                                          : 'hidden',
                                  settings: {
                                    autocomplete:
                                      (valuesClient || []).length > 10,
                                    emptyElement: t('forms.selectors.select'),
                                    options:
                                      filterValidClients(valuesClient) || [],
                                  },
                                },
                                {
                                  display: {
                                    breakpoints: {
                                      md: 6,
                                      sm: 12,
                                      xs: 12,
                                    },
                                  },
                                  label: t('forms.signers.fields.telefono'),
                                  name: 'telefono',
                                  type:
                                    selectorType !==
                                      t('forms.selectors.select') &&
                                    selectorType === 1
                                      ? 'text'
                                      : selectorType === 2 &&
                                        selectorClient !==
                                          t('forms.selectors.select')
                                        ? 'select'
                                        : selectorType === 3 &&
                                        selectorRelationShip !==
                                          t('forms.selectors.select')
                                          ? 'select'
                                          : 'hidden',
                                  settings: {
                                    autocomplete:
                                      (valuesClient || []).length > 10,
                                    emptyElement: t('forms.selectors.select'),
                                    options:
                                      filterValidClients(valuesClient) || [],
                                  },
                                },
                                {
                                  display: {
                                    breakpoints: {
                                      md: 6,
                                      sm: 12,
                                      xs: 12,
                                    },
                                  },
                                  label: t('forms.signers.fields.description'),
                                  name: 'description',
                                  type:
                                    selectorType !==
                                      t('forms.selectors.select') &&
                                    selectorType === 1
                                      ? 'text'
                                      : selectorType === 2 &&
                                          selectorClient !==
                                            t('forms.selectors.select')
                                        ? 'text'
                                        : selectorType === 3 &&
                                        selectorRelationShip !==
                                          t('forms.selectors.select')
                                          ? 'text'
                                          : 'hidden',
                                  disabled: readOnly,
                                },
                                {
                                  display: {
                                    breakpoints: {
                                      md: 12,
                                      sm: 12,
                                      xs: 12,
                                    },
                                  },
                                  type: 'space',
                                },
                              ]}
                              id="frmAddSigner"
                              initialValues={{
                                name: '',
                                email: '',
                                telefono: '',
                                description: '',
                              }}
                              handleSubmitData={handleSubmitData}
                              validationSchema={
                                selectorType === 1
                                  ? addSignerFormSchema
                                  : selectorType === 2
                                    ? addSignerFormSchemaClient
                                    : addSignerFormSchemaRelationship
                              }
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </Modal>
              )}
              {!readOnly && (
                <Modal
                  children={
                    <>
                      {t('forms.messages.signer_deleting_question')}{' '}
                      <b>{modal2?.data?.name}</b>?
                    </>
                  }
                  open={modal2?.open}
                  title={t('actions.delete_signer')}
                  actions={[
                    {
                      color: 'error',
                      label: t('actions.yes'),
                      fn: async function () {
                        handleRemoveSigner(modal2?.data?.id);
                      },
                    },
                  ]}
                  events={{
                    onClose: () => setModal2(),
                  }}
                />
              )}
            </Box>
          </Grid>

          {openSidebar && !readOnly && (
            <Grid item md={4} xs={12}>
              <Box
                className={classes.boxContainer}
                style={{
                  width: '100%',
                }}
              >
                <IconButton onClick={handleSidebarToggle}>
                  <CloseIcon />
                </IconButton>
                <Tabs
                  value={tabIndex}
                  onChange={(e, newValue) => setTabIndex(newValue)}
                  centered
                >
                  <Tab label={t('actions.label')} />
                  <Tab label="Firmas" />
                  <Tab label={t('actions.help')} />
                </Tabs>
                <Box
                  sx={{
                    marginTop: 16,
                  }}
                >
                  {tabIndex === 0 && (
                    <>
                      {data && labelsMatch && Object.keys(labelsMatch).length > 0 && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          py={3}
                          px={2}
                          bgcolor="#e0f7fa"
                          borderRadius={2}
                          boxShadow={1}
                          mb={2}
                        >
                          <Typography
                            variant="body1"
                            align="center"
                            color="textPrimary"
                          >
                            {t('messages.labels_found_one')}
                          </Typography>
                          <Typography
                            variant="body2"
                            align="center"
                            color="textSecondary"
                          >
                            {t('messages.labels_found_two')}
                          </Typography>
                        </Box>
                      )}
                      {data && labelsMatch && Object.keys(labelsMatch).length > 0 ? (
                        Object.keys(labelsMatch)
                          .filter((key) => !isSignatureToken(key))
                          .sort()
                          .map((key) => {
                            const cleanedKey = cleanString(key);
                            return (
                              <DynamicForm
                                key={`${key}-${labelsRevision}`}
                                container="card"
                                initialValues={{
                                  [cleanedKey]: labelsMatch[key] ? cleanTemplate(labelsMatch[key]) : '',
                                  [`functions_${cleanedKey}`]:
                                    fieldFunction[key] || [],
                                }}
                                fields={[
                                  {
                                    label: key,
                                    type: 'divider',
                                  },
                                  {
                                    display: {
                                      breakpoints: {
                                        md: 9,
                                        sm: 9,
                                        xs: 9,
                                      },
                                    },
                                    events: {
                                      onChange: ({ value }) => {
                                        setLabels((prevLabels) => ({
                                          ...prevLabels,
                                          [key]: value,
                                        }));
                                        setLabelsMatch((prevLabels) => ({
                                          ...prevLabels,
                                          [key]: value,
                                        }));
                                      },
                                    },
                                    label: 'Campos',
                                    name: cleanedKey,
                                    settings: {
                                      emptyElement: 'Selecciona',
                                      autocomplete: true,
                                      tree: true,
                                      options: data,
                                    },
                                    type: 'select',
                                  },
                                  {
                                    name: 'showFunctions',
                                    type: 'switch',
                                    events: {
                                      onChange: ({ value }) => {
                                        setOpenFunctions((prev) => ({
                                          ...prev,
                                          [cleanedKey]: !prev[cleanedKey],
                                        }));
                                      },
                                    },
                                    IconStateOne: openFunctions[cleanedKey]
                                      ? CloseIcon
                                      : PlaylistAddIcon,
                                    IconStateTwo: openFunctions[cleanedKey]
                                      ? CloseIcon
                                      : PlaylistAddIcon,
                                    tooltipStateOne: openFunctions[cleanedKey]
                                      ? t('actions.collapse')
                                      : t('actions.expand'),
                                    tooltipStateTwo: openFunctions[cleanedKey]
                                      ? t('actions.collapse')
                                      : t('actions.expand'),
                                    display: {
                                      variant: 'button',
                                      breakpoints: {
                                        md: 3,
                                        sm: 3,
                                        xs: 3,
                                      },
                                    },
                                  },
                                  {
                                    label: 'Funciones',
                                    name: `functions_${cleanedKey}`,
                                    type: !openFunctions[cleanedKey]
                                      ? 'hidden'
                                      : 'multiselecttag',
                                    settings: {
                                      options: functions?.map((option) => ({
                                        id: option.id,
                                        name: option.name,
                                        description: option.description,
                                      })),
                                    },
                                    display: {
                                      breakpoints: {
                                        md: 12,
                                        sm: 12,
                                        xs: 12,
                                      },
                                    },
                                    events: {
                                      onChange: ({ value }) => {
                                        setFieldFunction((prevFunctions) => ({
                                          ...prevFunctions,
                                          [key]: value,
                                        }));
                                      },
                                    },
                                  },
                                ]}
                              />
                            );
                          })
                      ) : (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          py={3}
                          px={2}
                          bgcolor="#f5f5f5"
                          borderRadius={2}
                          boxShadow={1}
                        >
                          <Typography
                            variant="body1"
                            align="center"
                            color="textSecondary"
                          >
                            {t('messages.lables_not_found_one')}
                          </Typography>
                          <Typography
                            variant="body2"
                            align="center"
                            color="textSecondary"
                          >
                            {t('messages.lables_not_found_one')}
                          </Typography>
                        </Box>
                      )}
                      {pdfFile && (
                        <>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            mt={2}
                            px={2}
                          >
                            {isAdding ? (
                              <>
                                <TextField
                                  label={t('forms.selectors.new_field')}
                                  variant="outlined"
                                  size="small"
                                  value={newLabel}
                                  onChange={(e) => setNewLabel(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) =>
                                    e.key === 'Enter' && handleAddLabel()
                                  }
                                  onBlur={() => setIsAdding(false)}
                                />
                                <Tooltip title={t('actions.add')}>
                                  <IconButton
                                    color="primary"
                                    onClick={handleAddLabel}
                                    sx={{
                                      ml: 1,
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title={t('actions.add_field')}>
                                <IconButton
                                  color="primary"
                                  onClick={() => setIsAdding(true)}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </>
                      )}
                    </>
                  )}
                  {tabIndex === 1 && (
                    <Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        py={3}
                        px={2}
                        bgcolor="#e0f7fa"
                        borderRadius={2}
                        boxShadow={1}
                        mb={2}
                      >
                        <Typography variant="body1" align="center" color="textPrimary">
                          A continuación, se muestran los marcadores de firma encontrados.
                        </Typography>
                        <Typography variant="body2" align="center" color="textSecondary">
                          Selecciona el firmante correspondiente para cada marcador.
                        </Typography>
                      </Box>
                      {signatureAnchors.length > 0 ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Firmas del documento
                          </Typography>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Asigna un firmante a cada marcador de firma.
                          </Typography>
                          {signatureAnchors.map((anchor) => {
                            const currentSignature = signatures.find(
                              (signature) => signature.sign === anchor.token
                                && signature.page === anchor.page
                                && signature.sign_occurrence === anchor.occurrence,
                            );

                            return (
                              <Box
                                key={anchor.key}
                                mb={2}
                                p={1.5}
                                border="1px solid #e0e0e0"
                                borderRadius={1}
                              >
                                <Typography variant="body2" gutterBottom>
                                  {anchor.token}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Página {anchor.page}
                                </Typography>
                                <Box mt={1}>
                                  <SelectControl
                                    label="Firmante"
                                    value={currentSignature?.signer_id || ''}
                                    onChange={({ target }) => {
                                      const signer = signers.find(
                                        (item) => String(item.id) === String(target.value),
                                      );
                                      handleAssignSignature(anchor, signer);
                                    }}
                                    settings={{
                                      emptyElement: 'Selecciona un firmante',
                                      options: signers.map((signer) => ({
                                        id: signer.id,
                                        name: signer.name,
                                      })),
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </>
                      ) : (
                        <Box py={3} textAlign="center">
                          <Typography variant="body1" color="textSecondary">
                            No se encontraron marcadores de firma.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Usa el formato {'{{sign:client1}}'} dentro del documento.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {tabIndex === 2 && (
                    <>
                      <Box>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                              ¿Cómo debo nombrar una variable de firma?
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2">
                              Usa el prefijo <strong>sign:</strong> seguido del nombre del campo. Por ejemplo:{' '}
                              <code>{'{{sign:firmante_numero1}}'}</code>.
                              También puedes usar nombres descriptivos como{' '}
                              <code>{'{{sign:cliente_principal}}'}</code> o{' '}
                              <code>{'{{sign:aval_numero1}}'}</code>.
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                              variant="h6"
                              style={{
                                marginBottom: 16,
                              }}
                            >
                              {t('messages.question_one')}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography>
                              <Stepper orientation="vertical">
                                {stepsQuestion1.map((label, index) => (
                                  <Step
                                    key={index}
                                    active={true}
                                    completed={stepsCompleted[index]}
                                  >
                                    <StepLabel
                                      optional={
                                        index === 3 ? (
                                          <Typography variant="caption">
                                            {t('messages.optional')}
                                          </Typography>
                                        ) : null
                                      }
                                    >
                                      <Typography variant="body2">
                                        {label}
                                      </Typography>
                                    </StepLabel>
                                  </Step>
                                ))}
                              </Stepper>
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                              {t('messages.question_two')}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box>
                              <p className={classes.helper}>
                                {t('messages.answer_quest_two')}
                              </p>
                              <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                py={3}
                                px={2}
                                bgcolor="#e3f2fd"
                                borderRadius={2}
                                boxShadow={1}
                              >
                                <p className={classes.helper}>
                                  {t('messages.example')}{' '}
                                  <strong>{`{{${t('messages.example_field')}}}`}</strong>
                                </p>
                              </Box>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                              {t('messages.question_three')}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stepper orientation="vertical">
                              {stepsQuestion2.map((step, index) => (
                                <Step key={index} active={true}>
                                  <StepLabel icon={step.icon}>
                                    <Typography variant="body2">
                                      {step.label}
                                    </Typography>
                                  </StepLabel>
                                </Step>
                              ))}
                            </Stepper>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
}

FileAndHtmlControl.defaultProps = DEFAULT_PROPS;
FileAndHtmlControl.propTypes = PROP_TYPES;

export default FileAndHtmlControl;
